import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslate } from '@bynder/localization';

import { IconCaretDown, IconMoreVertical } from '@bynder/icons';
import { InfoContext } from '../../context';
import { NavItem } from '../../types';
import { FadeIn } from '../FadeIn';

/*
 * bynder-platform/bynder/views/navigation/header.cfm
 */
interface Props {
	onReady: () => void;
}

export function NavItems({ onReady }: Props) {
	const [isVisible, setIsVisible] = useState(false);
	const { navItems } = useContext(InfoContext);
	const { translate } = useTranslate();
	const [activeItemId, setActiveItemId] = useState('');

	useEffect(() => {
		if (navItems?.length === 0) {
			return;
		}

		setIsVisible(true);
	}, [navItems, translate, isVisible]);

	useEffect(() => {
		if (isVisible) {
			onReady(); // Call on the set state callback
		}
	}, [isVisible, onReady]);

	function withoutSharp(linkFragment: string) {
		return linkFragment?.length ? linkFragment.split('#')[0] : '';
	}

	function withoutQueries(linkFragment: string) {
		return linkFragment?.length ? linkFragment.split('?')[0] : '';
	}

	function withoutTrailingSlash(url: string) {
		if (url.length > 0 && url[url.length - 1] === '/') {
			return url.substring(0, url.length - 1);
		}

		return url;
	}

	function withoutStartingSlash(url: string) {
		if (url.length > 0 && url[0] === '/') {
			return url.substring(1, url.length);
		}

		return url;
	}

	const calculateActiveStatus = useCallback(() => {
		const windowLocationPath = window.location.href.substring(window.location.origin.length);
		const windowLocationWithoutHashBang = windowLocationPath.replace(
			new RegExp(/\/#\/|\/#!/g),
			'/',
		);

		// Handle guidelines/home special case
		if (windowLocationWithoutHashBang.includes('/guidelines/home')) {
			const guidelinesHomeItem = navItems.find((item) => item.url === '/');

			if (guidelinesHomeItem) {
				setActiveItemId(guidelinesHomeItem.id);

				return;
			}
		}

		const locationTerms = windowLocationWithoutHashBang
			.split('/')
			.filter((term) => term !== '');

		let activeId = '';
		let previouslyMatchedTerms = 0;

		const perfectMatchNavItem = navItems.find((navItem) => {
			return withoutStartingSlash(windowLocationPath) === withoutStartingSlash(navItem.url);
		});

		if (perfectMatchNavItem) {
			previouslyMatchedTerms = perfectMatchNavItem.url.split('/').filter((fragment) => {
				return fragment?.length;
			}).length;
			activeId = perfectMatchNavItem.id;
		}

		const urlSubPartCoveredCompletely = (windowPath: string, navItemUrl: string) => {
			return (
				windowPath.includes(navItemUrl) ||
				withoutSharp(windowPath).includes(withoutTrailingSlash(withoutQueries(navItemUrl)))
			);
		};

		navItems.forEach((item) => {
			const itemUrlNoHashBang = item.url.replace(new RegExp(/\/#\/|\/#!/g), '/');

			const isDashboard =
				!locationTerms.length ||
				`${locationTerms[0]}/${locationTerms[1]}` === 'account/dashboard';

			if (isDashboard && itemUrlNoHashBang === '/') {
				previouslyMatchedTerms = 2;
				activeId = item.id;
			}

			const matchedSameOrMoreItemsBefore = locationTerms.length <= previouslyMatchedTerms;
			const navItemTerms = itemUrlNoHashBang
				.split('/')
				.filter((term) => withoutSharp(term) !== '');

			const evenFirstItemDoesntMatch = navItemTerms[0] !== withoutSharp(locationTerms[0]);

			if (matchedSameOrMoreItemsBefore || evenFirstItemDoesntMatch) {
				return;
			}

			const matchesCompletely = urlSubPartCoveredCompletely(windowLocationPath, item.url);

			if (!matchesCompletely) {
				return;
			}

			navItemTerms
				.filter((term) => term.length)
				.forEach((itemUrlFragment, index) => {
					if (
						itemUrlFragment === withoutSharp(locationTerms[index]) &&
						previouslyMatchedTerms <= index
					) {
						previouslyMatchedTerms = index + 1;
						activeId = item.id;
					}
				});
		});

		setActiveItemId(activeId);
	}, [navItems]);

	useEffect(() => {
		calculateActiveStatus();
	}, [navItems, calculateActiveStatus]);

	return isVisible ? (
		<FadeUp className="nav-bar-right clearfix">
			<nav className="clearfix">
				<ul className="nav-main clearfix">
					{navItems.map((item) => {
						const isActive = activeItemId === item.id;

						return <Item item={item} isActive={isActive} key={item.id} />;
					})}

					<li className="hidden-nav is-visible">
						<a href="#" title={translate('generalMore')}>
							<IconMoreVertical size="20px" /> {translate('generalMore')}...{' '}
							<IconCaretDown size="20px" />
						</a>
						<ul />
					</li>
				</ul>
			</nav>
		</FadeUp>
	) : null;
}

interface ItemProps {
	item: NavItem;
	isActive: boolean;
}

function Item({ item, isActive }: ItemProps) {
	const isAbsolute = /^https?:\/\//i.test(item.url);

	return (
		<li>
			<a
				href={item.url}
				title={item.label}
				target={isAbsolute ? '_blank' : ''}
				className={isActive ? 'active' : ''}
				rel="noreferrer"
			>
				{item.label}
			</a>
		</li>
	);
}

const FadeUp = (props: { children: React.ReactNode; className: string }) => (
	<FadeIn className={props.className}>{props.children}</FadeIn>
);
